import { defineStore } from "pinia";
import { ref } from "vue";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

const DEVICE_STORAGE_KEY = "jukebox_device_id";
const FINGERPRINT_STORAGE_KEY = "jukebox_fingerprint_id";
const FINGERPRINT_TIMEOUT_MS = 1500;

// Generate a random suffix to ensure uniqueness even when fingerprints collide
// (open-source FingerprintJS has ~60% accuracy — users with same phone model,
// browser version and screen resolution will get identical visitorIds)
const generateRandomSuffix = () => Math.random().toString(36).slice(2, 11);

const generateFallbackId = () => `device_${Date.now()}_${generateRandomSuffix()}`;

const withTimeout = (promise, timeoutMs) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error("Fingerprint timeout")), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

const resolveFingerprintId = async () => {
  const cached = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
  if (cached) {
    return cached;
  }

  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    if (result?.visitorId) {
      localStorage.setItem(FINGERPRINT_STORAGE_KEY, result.visitorId);
      return result.visitorId;
    }
  } catch (error) {
    console.warn("Fingerprint generation failed:", error?.message || error);
  }

  return null;
};

export const useDeviceStore = defineStore("device", () => {
  const deviceId = ref(null);

  const initDeviceId = async () => {
    const stored = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (stored) {
      deviceId.value = stored;
      return stored;
    }

    let fingerprintId = null;
    try {
      fingerprintId = await withTimeout(resolveFingerprintId(), FINGERPRINT_TIMEOUT_MS);
    } catch (error) {
      fingerprintId = null;
    }

    // Combine fingerprint with a random suffix to prevent collisions.
    // Without this, two users with identical browser fingerprints (common at
    // concerts where many people use the same iPhone model + Safari version)
    // would share the same deviceId and see each other's votes.
    const id = fingerprintId
      ? `fp_${fingerprintId}_${generateRandomSuffix()}`
      : generateFallbackId();
    localStorage.setItem(DEVICE_STORAGE_KEY, id);
    deviceId.value = id;
    return id;
  };

  const getDeviceId = async () => {
    if (!deviceId.value) {
      return initDeviceId();
    }
    return deviceId.value;
  };

  return {
    deviceId,
    initDeviceId,
    getDeviceId
  };
});
